import * as React from 'react';
import classNames from 'classnames';
import IntersectionObserver from 'react-intersection-observer';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {Section, Flex, RichIcon, Text, Link, NOOP} from 'components/UIKit';
import {ListingItemIncludes, ListingSocialMedia} from '../../types';
import {ListingMetric} from '../ListingMetrics';
import {CircleIcon} from './components/CircleIcon';
import styles from './ListingIncludes.scss';
import * as locales from './locales';

export interface ListingIncludesProps {
  id?: string;
  socialMediaAccounts: ListingSocialMedia[];
  includes: ListingItemIncludes[];
  mailingList?: number;
  socialFollowers: number | null;
  onVisibilityChange?: any;
  isPrivate: boolean;
  isStarterShop: boolean;
}

const PLATFORM_ICON_MAP = {
  pinterest: 'pinterest',
  twitter: 'twitter',
  instagram: 'instagram',
  facebook: 'facebook',
};

class ListingIncludesComponent extends React.Component<ListingIncludesProps & WithI18nProps> {
  handleViewportChange = (inView) => {
    const {onVisibilityChange = NOOP, id} = this.props;

    onVisibilityChange(id, inView);
  };

  render() {
    const {
      id,
      i18n,
      socialMediaAccounts,
      includes,
      mailingList,
      socialFollowers,
      isPrivate,
      isStarterShop,
    } = this.props;

    const showSocialMediaAccounts = socialMediaAccounts.length > 0;
    const showMailing = Boolean(mailingList);
    const showIncludes = includes.length > 0;
    const nonStarterStoreFeatures = ['included_inventory', 'supplier_relationships_transferrable'];

    const socialMediaDetails = (
      <Flex className={styles.SocialMedia} wrap="wrap">
        <ListingMetric
          metric={{
            value: String(socialFollowers) || '',
            title: i18n.translate('socialFollowers'),
          }}
          suffix={i18n.translate('subscribers')}
          wrapperComponent="div"
        />
        {showSocialMediaAccounts && (
          <Flex wrapperComponent="ul">
            {socialMediaAccounts.map((socialInfo) => (
              <li key={socialInfo.platform} className={styles.Platform}>
                <Link resetMargin newTab url={socialInfo.link} rel="nofollow">
                  <RichIcon size={16} icon={PLATFORM_ICON_MAP[socialInfo.platform]} />
                </Link>
              </li>
            ))}
          </Flex>
        )}
      </Flex>
    );
    return (
      <IntersectionObserver threshold={0.3} rootMargin="-72px" onChange={this.handleViewportChange}>
        <Section
          id={id}
          heading={i18n.translate('saleIncludes')}
          spacing="medium"
          headingProps={{type: 'h5', component: 'h2'}}
          fullWidth
        >
          {showIncludes && (
            <ul className={styles.List}>
              {includes.map((includedItem) => (
                <li
                  key={includedItem.item}
                  className={classNames(styles.Item, {
                    [styles.Not]: !includedItem.included,
                  })}
                >
                  <CircleIcon
                    className={styles.Icon}
                    fill={includedItem.included ? '#603276' : '#fd3333'}
                    icon={includedItem.included ? 'checkbox' : 'cancel'}
                  />
                  <Flex direction="column" className={styles.Text}>
                    <Text type="h3" text={includedItem.label} component="h3" />
                    {includedItem.included &&
                      (includedItem.item === 'included_social_media' && !isPrivate
                        ? socialMediaDetails
                        : includedItem.description && (
                            <Text
                              className={styles.Content}
                              resetMargin
                              text={includedItem.description}
                            />
                          ))}
                    {!includedItem.included && (
                      <Text
                        className={styles.Content}
                        secondary
                        resetMargin
                        text={
                          nonStarterStoreFeatures.includes(includedItem.item) && isStarterShop
                            ? i18n.translate('notIncludedForStarters')
                            : i18n.translate('notIncluded')
                        }
                      />
                    )}
                  </Flex>
                </li>
              ))}
              <li className={classNames(styles.Item, {[styles.Not]: showMailing})}>
                <CircleIcon
                  className={styles.Icon}
                  fill={showMailing ? '#603276' : '#fd3333'}
                  icon={showMailing ? 'checkbox' : 'cancel'}
                />
                <Flex direction="column" className={styles.Text}>
                  <Text type="h3" text={i18n.translate('mailingList')} component="h3" />
                  {showMailing ? (
                    <ListingMetric
                      metric={{value: String(mailingList)}}
                      suffix={i18n.translate('subscribers')}
                      wrapperComponent="div"
                    />
                  ) : (
                    <Text
                      className={styles.Content}
                      secondary
                      resetMargin
                      text={i18n.translate('notIncluded')}
                    />
                  )}
                </Flex>
              </li>
            </ul>
          )}
        </Section>
      </IntersectionObserver>
    );
  }
}

export const ListingIncludes = withI18n({
  fallback: locales.en,
  id: 'ListingIncludes',
})(ListingIncludesComponent);

export default ListingIncludes;
